import React, { FunctionComponent } from 'react'
import { useTranslation } from 'react-i18next'

import {
  StyledTabButton,
  StyledTableFilterSearch,
  StyledTableFilterHeader,
  StyledSearch,
  StyledFilterGroupContainer,
  StyledFilterMobileFooter
} from './TableFilterSearch.styles'
import { ClinTheme } from '../../ClinTheme'
import { ClinButton } from '../../components/ClinButton'
import { ClinIcon } from '../../components/ClinIcon'
import { ClinIconPathName } from '../../components/ClinIcon/ClinIcon.paths'
import { ClinText } from '../../components/ClinText'
import { TypographyVariant } from '../../components/ClinText/ClinText.styles'
import { FacetDto } from '../../types/swaggerTypes'
import { FilterGroup } from '../FilterGroup'
import { IFilterGroupProps } from '../FilterGroup/FilterGroup'

interface ITableFilterSearchProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Slot to insert a searchInput - default goes here */
  searchInput?(): React.ReactNode
  /** Title to display when none selected */
  title?: string
  /** Beginning of title to display when filters are selected */
  selectedTitle?: string
  /** Array of filter groups to display */
  filterGroups?: IFilterGroupProps[]
  /** Expand and show all on default */
  isOpenOnMount?: boolean
  /** Expand and show all on default */
  isFiltering?: boolean
  /** Callback when a filter is selected */
  handleSelectFilterGroup?: (filter: FacetDto, filterGroupTitle: string) => void
  /** Callback when clear all is selected */
  handleClearAllFilters?: () => void
  /** Whether to display the search input */
  displaySearch?: boolean
  /** Whether to display the filter icon */
  displayFilterIcon?: boolean
  /** External control for expanded state - when provided, overrides internal state */
  isExpanded?: boolean
  /** Callback when expand/collapse state changes - used with isExpanded */
  onExpandedChange?: (expanded: boolean) => void
  /** Whether to always show the component expanded without toggle button */
  alwaysExpanded?: boolean
  /** Whether to display the clear button */
  displayClearButton?: boolean
  /** Whether to display the title */
  showTitle?: boolean
  /** Whether to show all filters */
  showAll?: boolean
  /** Whether to display the see more/less button */
  displaySeeButton?: boolean
  /** Whether to display a border */
  displayBorder?: boolean
  /** Styles for the filter groups */
  filterGroupStyles?: React.CSSProperties
}

export const TableFilterSearch: FunctionComponent<ITableFilterSearchProps> = ({
  title = `Advanced filtering`,
  selectedTitle = `Filters Applied`,
  filterGroups = [],
  isOpenOnMount = false,
  isFiltering = true,
  searchInput,
  handleSelectFilterGroup,
  handleClearAllFilters,
  displaySearch = true,
  displayFilterIcon = true,
  isExpanded,
  onExpandedChange,
  alwaysExpanded = false,
  displayClearButton = true,
  showTitle = true,
  showAll = false,
  displaySeeButton = true,
  displayBorder = true,
  filterGroupStyles = {}
}) => {
  const { t } = useTranslation()

  const [internalIsShowing, setInternalIsShowing] =
    React.useState<boolean>(isOpenOnMount)

  const isShowing = alwaysExpanded
    ? true
    : isExpanded !== undefined
    ? isExpanded
    : internalIsShowing

  const handleToggle = () => {
    if (alwaysExpanded) return

    const newState = !isShowing
    if (onExpandedChange) {
      onExpandedChange(newState)
    } else {
      setInternalIsShowing(newState)
    }
  }

  const getSelectedFilterCount = (filterGroups: IFilterGroupProps[]): number =>
    filterGroups.reduce(
      (sum, elt) =>
        sum + (elt && elt.selectedFilters ? elt.selectedFilters.length : 0),
      0
    )
  return (
    <StyledTableFilterSearch displayBorder={displayBorder}>
      <StyledTableFilterHeader
        displayBorder={displayBorder}
        isShowing={isShowing}
      >
        {isFiltering &&
          displayFilterIcon &&
          !alwaysExpanded &&
          filterGroups &&
          filterGroups.length > 0 && (
            <StyledTabButton
              areFiltersApplied={getSelectedFilterCount(filterGroups) > 0}
              onClick={handleToggle}
            >
              <ClinIcon
                iconName={
                  isShowing ? ClinIconPathName.Cross : ClinIconPathName.Filter
                }
              />
              <ClinText
                as="span"
                textTransform="uppercase"
                marginLeft={ClinTheme.space[2]}
                fontSize={ClinTheme.fontSizes[1]}
                fontWeight={ClinTheme.fontWeights.medium}
                color={ClinTheme.colors.primaryMid}
              >
                {getSelectedFilterCount(filterGroups) > 0
                  ? `${selectedTitle} (${getSelectedFilterCount(filterGroups)})`
                  : title}
              </ClinText>
            </StyledTabButton>
          )}
        {displaySearch && (
          <StyledSearch isFiltering={isFiltering}>
            <ClinText
              className="search"
              as="span"
              variant={TypographyVariant.Paragraph}
              fontWeight={ClinTheme.fontWeights.medium}
              textTransform="uppercase"
              color={ClinTheme.colors.primary}
              marginLeft={ClinTheme.space[4]}
            >
              {t('common:search')}
            </ClinText>
            {searchInput && searchInput()}
          </StyledSearch>
        )}
      </StyledTableFilterHeader>
      {filterGroups && filterGroups.length > 0 && (
        <StyledFilterGroupContainer>
          {isShowing && (
            <>
              {displayClearButton && (
                <ClinButton
                  as="div"
                  variant="linkButton"
                  className={'clear-btn'}
                  color={ClinTheme.colors.primary}
                  onClick={handleClearAllFilters}
                >
                  {t('common:buttons.clear_all')}
                </ClinButton>
              )}
              {filterGroups &&
                filterGroups.map((filter) => (
                  <FilterGroup
                    filterGroupStyles={filterGroupStyles}
                    showTitle={showTitle}
                    displaySeeButton={displaySeeButton}
                    showAll={showAll}
                    key={filter.title}
                    {...filter}
                    handleSelectFilter={(facet) =>
                      handleSelectFilterGroup &&
                      handleSelectFilterGroup(facet, filter.title)
                    }
                  />
                ))}
            </>
          )}
          {isShowing && (
            <StyledFilterMobileFooter>
              <ClinButton
                variant="secondary"
                onClick={() => {
                  if (alwaysExpanded) return

                  if (onExpandedChange) {
                    onExpandedChange(false)
                  } else {
                    setInternalIsShowing(false)
                  }
                  window.scrollTo({
                    behavior: 'smooth',
                    top: 0
                  })
                }}
              >
                {t('common:buttons.close')}
              </ClinButton>
            </StyledFilterMobileFooter>
          )}
        </StyledFilterGroupContainer>
      )}
    </StyledTableFilterSearch>
  )
}
